<!--
  @component ProvenanceIO — P1 control-plane lens.
  chain:   ctx.deployment.stream → this → ctx.provenance
  reads:   the parent node's unified-event stream as the canonical
           append-only timeline (the u1 fused ledger view).
  effects: NONE — chronological projection only.
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { PROVENANCE_IO_KEY } from "./deployment-io.js";
  import type { ProvenanceIOContext } from "./deployment-io.js";

  export { PROVENANCE_IO_KEY };
  export type { ProvenanceIOContext };

  export function useProvenanceIO(): ProvenanceIOContext {
    const ctx = getContext<ProvenanceIOContext | undefined>(
      PROVENANCE_IO_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useProvenanceIO() called outside of a ProvenanceIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  // The daemon appends in atMs order (its provenance discipline);
  // the lens trusts that order — no re-sort cost per access.
  const ctx: ProvenanceIOContext = {
    get events() {
      return parent.stream;
    },
    get latest() {
      const s = parent.stream;
      return s.length === 0 ? null : (s[s.length - 1] ?? null);
    },
    get count() {
      return parent.stream.length;
    },
  } satisfies ProvenanceIOContext;

  setContext(PROVENANCE_IO_KEY, ctx);
</script>

{@render children?.()}
